import React, { useState, useMemo, useRef, useEffect } from 'react';
import zipcelx from 'zipcelx';
import HighStock from 'highcharts/highstock';
import type { Options as HighchartsOptions } from 'highcharts';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import { useQuery } from 'react-query';
import { queryOptions } from '~/shared/constants';
import { fetchIndicatorData } from '~/shared/services/api';
import moment from 'moment';
import styled from 'styled-components';
import { AlertBar, Loader } from '~/shared/components';
import { highchartsTooltipCfg } from '~/shared/config/highchartsCfg';

const DATE_DIFF = 0;

HCExporting(HighStock);
HCExportData(HighStock);
HCNoData(HighStock);
HCOfflineExporting(HighStock);
HCAccessibility(HighStock);

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(HighStock);

const EMPTY_STOCK_OPTIONS: HighchartsOptions = { series: [] };

interface ISeriesObj {
  type: string;
  name: string;
  data: number[][];
  color: string;
}

const Root = styled.div`
  height: 400px;
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  // box-shadow: 0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%);
  border: 1px solid #e6e9eb;
  background-color: var(--background-chart);
  border-radius: 4px;
  padding: 10px;
`;

const AlignedLoader = styled(Loader)`
  left: calc(50% - 25px);
  top: calc(50% - 30px);
`;

export default function IndicatorStackedBarChart({
  category,
  prism,
  prismId,
  indicator,
  cellInfo,
  filterValues,
  tooltipCfg,
}: any) {
  const chartRef = useRef<any>(null);

  const [chartTitle, setChartTitle] = useState('');
  const [chartSubtitle, setChartSubtitle] = useState('');
  const [chartYtitle, setChartYtitle] = useState('');
  let exportFileName = `${cellInfo.topic ? `${cellInfo.topic}_` : ''}${
    cellInfo.symbol ? `${cellInfo.symbol}_` : ''
  }${indicator}`;
  Object.keys(filterValues).forEach(function (key) {
    if (key !== 'TimeResolution') exportFileName += `_${filterValues[key]}`;
  });

  const { data, error, isLoading } = useQuery(
    ['fetchIndicatorData', indicator, cellInfo, JSON.stringify(filterValues)],
    async () => {
      const obj = await fetchIndicatorData(category, prism, prismId, indicator, cellInfo, filterValues);
      const colorScheme = obj.color_scheme || [];
      setChartTitle(obj.title || '');
      setChartSubtitle(obj.subtitle || `${cellInfo.topic || ''} ${cellInfo.symbol || ''}`);
      setChartYtitle(obj.ytitle || '');
      const xLabels = obj.row_nodes.map((r: any) => r.name);
      const yLabels = obj.col_nodes.map((c: any) => c.name);
      const seriesData: any[] = [];
      for (let j = 0; j < yLabels.length; ++j) {
        const seriesObj: ISeriesObj = {
          type: 'column',
          name: yLabels[yLabels.length - j - 1],
          data: [],
          color: colorScheme ? colorScheme[yLabels.length - j - 1] : undefined,
        };
        for (let i = 0; i < xLabels.length; ++i) {
          const value: number = obj.data[i][yLabels.length - j - 1] as unknown as number;
          seriesObj.data.push([moment.utc(xLabels[i]).valueOf() - DATE_DIFF, value]);
        }
        seriesData.push(seriesObj);
      }
      return seriesData;
    },
    queryOptions,
  );

  const chartOptions = useMemo(() => {
    if (!data) return;
    return {
      chart: {
        type: 'column',
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
        },
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof HighStock.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'downloadPNG',
              'downloadSVG',
              'downloadPDF',
              'separator',
              'downloadXLS',
              'downloadCSV',
            ],
          },
        },
        fallbackToExportServer: false,
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 10,
        style: {
          fontSize: '16px',
          color: 'var(--color-chart-title)',
          fontWeight: 500,
          letterSpacing: '0.5px',
        },
      },
      subtitle: {
        text: chartSubtitle,
      },
      credits: {
        enabled: false,
      },
      yAxis: {
        endOnTick: false,
        opposite: false,
        min: 0,
        title: {
          text: chartYtitle,
        },
        stackLabels: {
          enabled: false,
        },
      },
      legend: {
        reversed: true,
        enabled: true,
        align: 'right',
        x: -30,
        verticalAlign: 'top',
        y: 0,
        floating: true,
        backgroundColor: HighchartsReact.defaultProps?.defaultOptions?.legend.backgroundColor || 'white',
        shadow: false,
      },
      tooltip: highchartsTooltipCfg(tooltipCfg),
      plotOptions: {
        series: {
          maxPointWidth: 30,
        },
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      rangeSelector: {
        selected: 5,
        inputEnabled: false,
      },
      series: data,
    };
  }, [data, chartTitle, chartSubtitle, chartYtitle, exportFileName, tooltipCfg]);

  useEffect(() => {
    if ((error as unknown as any)?.message) {
      chartRef.current?.showLoading((error as unknown as any)?.message);
    } else if (data?.length === 0 || !data) {
      chartRef.current?.showLoading('No Data');
    } else {
      chartRef.current?.hideLoading();
    }
  });

  return (
    <Root>
      <HighchartContainer>
        {isLoading ? (
          <AlignedLoader />
        ) : (
          <HighchartsReact
            containerProps={{ style: { height: '100%' } }}
            style={{ flex: 1 }}
            highcharts={HighStock}
            constructorType={'stockChart'}
            options={chartOptions ?? EMPTY_STOCK_OPTIONS}
            callback={(chart: any) => (chartRef.current = chart)}
          />
        )}
        {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      </HighchartContainer>
    </Root>
  );
}
